import { useEffect, useState } from 'react'
import type { FC } from 'react'

import { F0Dialog } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { GoalsTab } from './GoalsTab'
import { PastCoursesTab } from './PastCoursesTab'
import { PastFeedbacksTab } from './PastFeedbacksTab'
import { PastReviewsTab } from './PastReviewsTab'

const TAB_IDS = {
  GOALS: 'goals',
  PAST_REVIEWS: 'past_reviews',
  PAST_COURSES: 'past_courses',
  PAST_FEEDBACKS: 'past_feedbacks',
} as const

type ContextSidepanelTab = (typeof TAB_IDS)[keyof typeof TAB_IDS]

interface ContextSidepanelProps {
  open: boolean
  onClose: () => void
  initialTab?: ContextSidepanelTab
  employeeId: string
  targetAccessId: string
  reviewProcessStartedAt: string
  isPerformanceFeedbackEnabled: boolean
  canUpsellTrainings: boolean
}

export const ContextSidepanel: FC<ContextSidepanelProps> = ({
  open,
  onClose,
  initialTab = TAB_IDS.PAST_REVIEWS,
  employeeId,
  targetAccessId,
  reviewProcessStartedAt,
  isPerformanceFeedbackEnabled,
  canUpsellTrainings,
}) => {
  const [activeTab, setActiveTab] = useState<ContextSidepanelTab>(initialTab)
  const tabs = [
    {
      id: TAB_IDS.PAST_REVIEWS,
      label: i18n.t(
        'performance_process.reviews.answer_evaluation.context_sidepanel.tabs.past_reviews'
      ),
    },
    {
      id: TAB_IDS.PAST_COURSES,
      label: i18n.t('performance_process.targets.see_past_courses.title_panel'),
    },
    {
      id: TAB_IDS.GOALS,
      label: i18n.t('performance_process.reviews.answer_evaluation.goals_section.title'),
    },
    ...(isPerformanceFeedbackEnabled
      ? [
          {
            id: TAB_IDS.PAST_FEEDBACKS,
            label: i18n.t(
              'performance_process.reviews.answer_evaluation.context_sidepanel.tabs.past_feedbacks'
            ),
          },
        ]
      : []),
  ]

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab)
    }
  }, [open, initialTab])

  return (
    <F0Dialog
      isOpen={open}
      onClose={onClose}
      position="right"
      title={i18n.t('performance_process.reviews.answer_evaluation.context_sidepanel.title')}
      activeTabId={activeTab}
      setActiveTabId={setActiveTab}
      tabs={tabs}
    >
      {activeTab === TAB_IDS.PAST_REVIEWS && (
        <PastReviewsTab
          targetAccessId={targetAccessId}
          reviewProcessStartedAt={reviewProcessStartedAt}
        />
      )}

      {activeTab === TAB_IDS.PAST_COURSES && (
        <PastCoursesTab targetAccessId={targetAccessId} canUpsellTrainings={canUpsellTrainings} />
      )}

      {activeTab === TAB_IDS.GOALS && <GoalsTab employeeId={employeeId} />}

      {isPerformanceFeedbackEnabled && activeTab === TAB_IDS.PAST_FEEDBACKS && (
        <PastFeedbacksTab targetAccessId={targetAccessId} />
      )}
    </F0Dialog>
  )
}
